import type { AnalysisResults } from '../../services/api/novel-analysis';

type ParseFn = (str: string | null) => unknown;

export function StyleTab({ style, parseJson }: { style: AnalysisResults['writing_style']; parseJson: ParseFn }) {
  if (!style) return <p className="p-4 text-gray-500 text-sm">尚無風格分析資料</p>;

  const styleClassification = parseJson(style.style_classification) as {
    genre?: string; sub_genre?: string; tone?: string; core_characteristics?: string[];
  } | null;
  const vocabSystem = parseJson(style.vocabulary_system) as {
    recommended_terms?: string[]; forbidden_terms?: string[];
  } | null;
  const nsfwTechniques = parseJson(style.nsfw_techniques) as {
    escalation_model?: string; climax_formula?: string; psychological_techniques?: string[];
  } | null;
  const quantitative = parseJson(style.quantitative) as {
    avg_sentence_length?: number; dialogue_ratio?: number; description_density?: number;
    sentence_structure?: Record<string, unknown>; paragraph_analysis?: Record<string, unknown>;
    stylistic_metrics?: Record<string, unknown>;
  } | null;
  const samples = parseJson(style.sample_passages) as Array<{ category: string; excerpt: string; analysis: string }> | null;

  return (
    <div className="p-4 space-y-4">
      {/* Style Classification */}
      {styleClassification && (
        <div className="mb-4">
          <h3 className="font-medium text-sm mb-2">風格分類</h3>
          <div className="flex gap-2 flex-wrap mb-2">
            {styleClassification.genre && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">類型：{styleClassification.genre}</span>
            )}
            {styleClassification.sub_genre && (
              <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">子類型：{styleClassification.sub_genre}</span>
            )}
            {styleClassification.tone && (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">基調：{styleClassification.tone}</span>
            )}
          </div>
          {styleClassification.core_characteristics?.length ? (
            <div className="flex gap-1 flex-wrap">
              {styleClassification.core_characteristics.map((c, i) => (
                <span key={i} className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs">{c}</span>
              ))}
            </div>
          ) : null}
        </div>
      )}

      {renderSection('敘事聲音', style.narrative_voice, parseJson)}
      {renderSection('文筆特色', style.prose_characteristics, parseJson)}
      {renderSection('詞彙風格', style.vocabulary, parseJson)}
      {renderSection('節奏', style.pacing, parseJson)}
      {renderSection('NSFW 風格', style.nsfw_style, parseJson)}

      {/* Vocabulary System */}
      {vocabSystem && (
        <div className="mb-4">
          <h3 className="font-medium text-sm mb-2">詞彙系統</h3>
          {vocabSystem.recommended_terms?.length ? (
            <div className="mb-2">
              <span className="text-xs font-medium text-green-600">推薦用詞：</span>
              <div className="flex gap-1 flex-wrap mt-1">
                {vocabSystem.recommended_terms.map((t, i) => (
                  <span key={i} className="px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs">{t}</span>
                ))}
              </div>
            </div>
          ) : null}
          {vocabSystem.forbidden_terms?.length ? (
            <div>
              <span className="text-xs font-medium text-red-600">禁用詞彙：</span>
              <div className="flex gap-1 flex-wrap mt-1">
                {vocabSystem.forbidden_terms.map((t, i) => (
                  <span key={i} className="px-2 py-0.5 bg-red-50 text-red-700 rounded text-xs line-through">{t}</span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* NSFW Techniques */}
      {nsfwTechniques && (
        <div className="mb-4">
          <h3 className="font-medium text-sm mb-2">NSFW 技巧</h3>
          {nsfwTechniques.escalation_model && (
            <div className="text-sm mb-1">
              <span className="text-gray-500">升級模型：</span>
              <span className="text-gray-700">{nsfwTechniques.escalation_model}</span>
            </div>
          )}
          {nsfwTechniques.climax_formula && (
            <div className="text-sm mb-1">
              <span className="text-gray-500">高潮公式：</span>
              <span className="text-gray-700">{nsfwTechniques.climax_formula}</span>
            </div>
          )}
          {nsfwTechniques.psychological_techniques?.length ? (
            <div className="flex gap-1 flex-wrap mt-1">
              {nsfwTechniques.psychological_techniques.map((t, i) => (
                <span key={i} className="px-2 py-0.5 bg-pink-50 text-pink-700 rounded text-xs">{t}</span>
              ))}
            </div>
          ) : null}
        </div>
      )}

      {/* Quantitative */}
      {quantitative && (
        <div className="mb-4">
          <h3 className="font-medium text-sm mb-2">量化分析</h3>
          <div className="grid grid-cols-3 gap-2 mb-2">
            {quantitative.avg_sentence_length != null && (
              <MetricCard label="平均句長" value={`${quantitative.avg_sentence_length} 字`} />
            )}
            {quantitative.dialogue_ratio != null && (
              <MetricCard label="對話比例" value={`${Math.round(quantitative.dialogue_ratio * 100)}%`} />
            )}
            {quantitative.description_density != null && (
              <MetricCard label="描寫密度" value={`${Math.round(quantitative.description_density * 100)}%`} />
            )}
          </div>
          {quantitative.sentence_structure && renderKV('句式結構', quantitative.sentence_structure)}
          {quantitative.paragraph_analysis && renderKV('段落分析', quantitative.paragraph_analysis)}
          {quantitative.stylistic_metrics && renderKV('風格指標', quantitative.stylistic_metrics)}
        </div>
      )}

      {/* Sample Passages */}
      {samples && samples.length > 0 && (
        <div>
          <h3 className="font-medium text-sm mb-2">代表性段落</h3>
          {samples.map((s, i) => (
            <div key={i} className="mb-3 bg-gray-50 rounded p-3">
              <span className="text-xs font-medium text-blue-600">{s.category}</span>
              <blockquote className="text-sm text-gray-700 italic border-l-2 border-gray-300 pl-3 my-1">
                {s.excerpt}
              </blockquote>
              <p className="text-xs text-gray-500">{s.analysis}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function renderSection(title: string, data: unknown, parseJson: ParseFn) {
  if (!data) return null;
  const obj = typeof data === 'string' ? parseJson(data as string) : data;
  if (!obj || typeof obj !== 'object') return null;

  return (
    <div className="mb-4">
      <h3 className="font-medium text-sm mb-2">{title}</h3>
      <div className="space-y-1">
        {Object.entries(obj as Record<string, unknown>).map(([key, value]) => (
          <div key={key} className="text-sm">
            <span className="text-gray-500">{key}：</span>
            <span className="text-gray-700">
              {Array.isArray(value) ? value.join(', ') : String(value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function renderKV(title: string, obj: Record<string, unknown>) {
  return (
    <div className="mt-2">
      <span className="text-xs font-medium text-gray-500">{title}</span>
      <div className="space-y-0.5 mt-0.5">
        {Object.entries(obj).map(([k, v]) => (
          <div key={k} className="text-xs">
            <span className="text-gray-500">{k}：</span>
            <span className="text-gray-700">{Array.isArray(v) ? v.join(', ') : String(v)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-gray-50 rounded p-2 text-center">
      <span className="text-xs text-gray-500 block">{label}</span>
      <span className="text-lg font-medium text-gray-700">{value}</span>
    </div>
  );
}
